import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';

/** Decode a user identifier (npub, nprofile, hex) to a pubkey hex string. */
export function decodeUserToPubkey(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^[0-9a-fA-F]{64}$/.test(trimmed)) return trimmed.toLowerCase();
  if (trimmed.startsWith('nostr:')) return decodeUserToPubkey(trimmed.slice(6));
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch {
    return null;
  }
  return null;
}

export interface ProfileSearchResult {
  pubkey: string;
  name: string;
  picture: string;
  nip05?: string;
}

const SEARCH_RELAYS = [
  'wss://search.nos.today',
  'wss://relay.nostr.band',
  'wss://relay.noswhere.com',
];

export function useProfileSearch(query: string) {
  const { nostr } = useNostr();
  const q = query.trim();

  return useQuery<ProfileSearchResult[]>({
    queryKey: ['profile-search', q.toLowerCase()],
    queryFn: async (c) => {
      // Direct pubkey/npub paste → single result without relay search.
      const direct = decodeUserToPubkey(q);
      if (direct) {
        const [event] = await nostr.query([{ kinds: [0], authors: [direct], limit: 1 }], {
          signal: c.signal,
        });
        let name = `${direct.slice(0, 8)}…`;
        let picture = '';
        let nip05: string | undefined;
        if (event) {
          try {
            const meta = JSON.parse(event.content) as { name?: string; display_name?: string; picture?: string; nip05?: string };
            name = meta.display_name || meta.name || name;
            picture = meta.picture || '';
            nip05 = meta.nip05;
          } catch { /* keep defaults */ }
        }
        return [{ pubkey: direct, name, picture, nip05 }];
      }

      if (q.length < 2) return [];

      const group = nostr.group(SEARCH_RELAYS);
      // Hoisted so the NIP-50 `search` field doesn't trip excess-property checks.
      const filter = { kinds: [0], search: q, limit: 40 };
      const events = await group.query([filter], {
        signal: c.signal,
      });

      const results: ProfileSearchResult[] = [];
      const seen = new Set<string>();
      for (const event of events) {
        if (seen.has(event.pubkey)) continue;
        seen.add(event.pubkey);
        try {
          const meta = JSON.parse(event.content) as { name?: string; display_name?: string; picture?: string; nip05?: string };
          const name = meta.display_name || meta.name || '';
          if (!name) continue;
          results.push({
            pubkey: event.pubkey,
            name,
            picture: meta.picture || '',
            nip05: meta.nip05,
          });
        } catch { /* skip unparsable */ }
        if (results.length >= 8) break;
      }
      return results;
    },
    enabled: q.length >= 2 || decodeUserToPubkey(q) !== null,
    staleTime: 60 * 1000,
  });
}
